import React from 'react';
import { LayoutContext, AuthContext } from 'contexts';
import { Space, Avatar, TableProps, Icon } from 'components';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { useHistory } from 'react-router-dom';
import { UserRoles, userRolesToLabelMap } from '@bb/users';
import { isEqual } from 'lodash';
import { useTableResizer } from 'hooks';
import { StyledTable, UserStatusTag } from '../../../components';
import { addDatacy, addDatacyToHeader } from '../util';
import { PAGE_SIZE } from './constants';
import ManageUsersContext from '../ManageUsersContext';
import { DeletedUser } from '../Hooks';
import BranchFilterDropdown from './BranchFilterDropdown';
import ManageUsersPagination from './ManageUsersPagination';

const DeletedTable: React.FC = () => {
  const { transition } = React.useContext(LayoutContext);
  const { auth } = React.useContext(AuthContext);
  const {
    isDeletedLoading: isLoading,
    deletedUsers,
    deletedUsersCount,
    existingDeletedUserRoles,
    roleFilters,
    setRoleFilters,
    locations,
    pageNumber,
    setPageNumber,
  } = React.useContext(ManageUsersContext);
  const [sortedUsers, setSortedUsers] = React.useState(deletedUsers);
  const [sort, setSort] = React.useState<{
    key: keyof DeletedUser;
    order: number;
  }>({ key: 'firstName', order: 1 });
  const history = useHistory();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tableWrapperRef = React.useRef<HTMLDivElement>();

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
  });

  React.useEffect(() => {
    setSortedUsers(deletedUsers);
    if (sort.key) {
      const { key, order } = sort;
      setSortedUsers(
        [...deletedUsers].sort((a: DeletedUser, b: DeletedUser) => {
          const stringProcessor = (user: DeletedUser) => {
            if (key === 'location')
              return auth?.org?.locations?.[
                +user.location
              ]?.name?.toLowerCase();
            if (key === 'address')
              return auth?.org?.addresses?.[+user.location]?.[
                +user.address
              ].address?.toLowerCase();
            return String(user[key]).toLowerCase();
          };
          return stringProcessor(a) > stringProcessor(b) ? order : -order;
        }),
      );
    } else {
      setSortedUsers(deletedUsers);
    }
  }, [auth?.org?.addresses, auth?.org?.locations, deletedUsers, sort]);

  const columns: TableProps<DeletedUser>['columns'] = [
    {
      width: 250,
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
      render: (name: string, user) => (
        <Space align="center" size="middle">
          <Avatar user={user} />
          {name}
        </Space>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort.key === 'firstName'
        ? { defaultSortOrder: sort.order === -1 ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('deleted-name'),
      onHeaderCell: addDatacyToHeader('deleted-name'),
    },
    {
      width: 100,
      title: 'Role',
      key: 'role',
      dataIndex: 'role',
      align: 'center',
      filteredValue: roleFilters,
      filters: existingDeletedUserRoles.map(
        role => ({
          text: role,
          value: role,
        }),
        [],
      ),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={roleFilters?.length ? 'primary' : undefined}
        />
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort.key === 'role'
        ? { defaultSortOrder: sort.order === -1 ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('deleted-role'),
      onHeaderCell: addDatacyToHeader('deleted-role'),
    },
    {
      width: 90,
      title: 'Status',
      key: 'status',
      dataIndex: '',
      align: 'center',
      render: () => <UserStatusTag isDeleted />,
      onCell: addDatacy('deleted-status'),
      onHeaderCell: addDatacyToHeader('deleted-status'),
    },
    {
      width: 200,
      title: 'Email',
      key: 'email',
      dataIndex: 'email',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort.key === 'role'
        ? { defaultSortOrder: sort.order === -1 ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('deleted-email'),
      onHeaderCell: addDatacyToHeader('deleted-email'),
    },
    {
      width: 125,
      title: 'Branch',
      key: 'location',
      dataIndex: 'location',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort.key === 'role'
        ? { defaultSortOrder: sort.order === -1 ? 'descend' : 'ascend' }
        : {}),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={locations?.length ? 'primary' : undefined}
        />
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <BranchFilterDropdown {...props} />
      ),
      onCell: addDatacy('deleted-branch'),
      onHeaderCell: addDatacyToHeader('deleted-branch'),
    },
    {
      width: 300,
      title: 'Address',
      key: 'address',
      dataIndex: 'address',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort.key === 'role'
        ? { defaultSortOrder: sort.order === -1 ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('deleted-address'),
      onHeaderCell: addDatacyToHeader('deleted-address'),
    },
  ];
  const processedColumns = columns.filter(Boolean);

  const dataSource =
    auth &&
    sortedUsers
      ?.slice((pageNumber - 1) * PAGE_SIZE, pageNumber * PAGE_SIZE)
      ?.map(
        ({
          id,
          firstName,
          lastName,
          email,
          location,
          address,
          role,
          avatarFilestackId,
        }) => ({
          id,
          key: id,
          firstName,
          lastName,
          name: `${firstName} ${lastName}`,
          email,
          role: userRolesToLabelMap[role],
          location: auth?.org?.locations?.[+location]?.name,
          address: auth?.org?.addresses?.[+location]?.[+address].address,
          avatarFilestackId,
        }),
      );

  return (
    <>
      <div ref={tableWrapperRef}>
        <StyledTable
          style={{ transition: `all ${transition}s` }}
          tableLayout="fixed"
          $height={tableHeight}
          $headerHeight={tableHeaderHeight}
          pagination={false}
          showSorterTooltip={false}
          loading={isLoading}
          dataSource={dataSource}
          columns={processedColumns}
          scroll={{
            x: '100%',
            y: `calc(100% - ${tableHeaderHeight}px)`,
          }}
          onChange={(
            pagination,
            filters,
            sorter: { column: any; order: any },
          ) => {
            const newRoleFilters = filters.role ?? [];
            if (!isEqual(roleFilters, newRoleFilters)) {
              setRoleFilters(newRoleFilters as (keyof typeof UserRoles)[]);
            }

            if (sorter.column) {
              const key: keyof DeletedUser = sorter.column.dataIndex;
              const order = sorter.order === 'descend' ? -1 : 1;
              setSort({ key, order });
            } else {
              setSort({ key: 'firstName', order: 1 });
            }
            setPageNumber(1);
          }}
          onRow={user => ({
            onClick: () => history.push(`/users/edit/${user.id}`),
          })}
          data-cy="deleted-table"
        />
        <ManageUsersPagination count={deletedUsersCount} tableName="deleted" />
      </div>
    </>
  );
};

export default DeletedTable;
